<script lang="ts" setup>
import { computed } from 'vue'
import type { WelfarePageConfig } from '../configs/types'
import WelfareGuideAddScreen from './modules/WelfareGuideAddScreen.vue'
import WelfareGuideOpen from './modules/WelfareGuideOpen.vue'

const props = defineProps<{
  config: WelfarePageConfig
  homeBgUrl: string
  btnBgColor: string
  signed: boolean
  progress: any[]
  modalSigned: boolean
  progressInfo: any
  tomorrowInfo: any
  checkInDayNum: number
  showTabbar: boolean
  tasks: any[]
  showGuidePopupAddHome: boolean
  showGuideOpen: boolean
  finishPopup: boolean
  coins: number
  completeTaskDescription: string
  userCoinBalance: number
  getBtnStatus: (item: any) => string
  onCheckIn: () => void
  handleTask: (item: any) => void
  onCheckInOk: () => void
}>()

const emit = defineEmits<{
  (e: 'update:modal-signed', value: boolean): void
  (e: 'update:show-guide-popup-add-home', value: boolean): void
  (e: 'update:show-guide-open', value: boolean): void
  (e: 'update:finish-popup', value: boolean): void
}>()

// 使用 computed 实现 v-model 双向绑定
const modalSignedLocal = computed({
  get: () => props.modalSigned,
  set: (val: boolean) => emit('update:modal-signed', val),
})

const finishPopupLocal = computed({
  get: () => props.finishPopup,
  set: (val: boolean) => emit('update:finish-popup', val),
})

const coinsGradient = computed(() => props.config.theme.coinsGradient)
const actionGradient = computed(() => props.config.theme.actionGradient)
const checkinBtnGradient = computed(() => props.config.theme.checkinBtnGradient)
</script>

<template>
  <div class="min-h-100dvh pb-20">
    <!-- Cover -->
    <div class="cover w-full relative" :style="{ backgroundImage: homeBgUrl }">
      <!-- 金币显示区域 - 左侧布局 -->
      <div class="flex items-center px-14px pt-3.5 pb-3">
        <img class="w-24px h-24px" :src="config.resources.welfareGold">
        <strong class="coins-gradient ml-5px">{{ userCoinBalance }}</strong>
        <span class="ml-5px text-12px text-n-text-2">{{ config.texts.coins }}</span>
      </div>

      <!-- Daily Check-in 卡片 -->
      <div class="checkin-card mx-14px px-10px py-16px bg-n-bg-card">
        <h3 class="text-18px text-n-text-1 font-bold">
          {{ config.texts.dailyCheckIn }}
        </h3>
        <p class="text-12px text-n-text-3">
          {{ config.texts.youHaveCheckedIn }} {{ checkInDayNum }} {{ config.texts.dayStraight }}
        </p>
        <div class="mt-15px px-4px grid grid-cols-7 space-x-8px">
          <div
            v-for="(item, index) in progress"
            :key="item.num"
            class="text-center"
            :class="{ 'opacity-70': item.isSign }"
          >
            <div class="flex flex-col items-center pt-10px pb-6px bg-n-bg-tag b-rd-5px">
              <img v-if="index !== progress.length - 1" class="w-24px h-24px" :src="config.resources.welfareGold">
              <img v-else class="w-28px h-25px mb--1px" :src="config.resources.welfareBox">
              <span class="mt-3px text-10px text-n-text-1">+{{ item.prizeNum }}</span>
            </div>
            <div class="flex justify-center">
              <i-assets-icon-right v-if="item.isSign" class="mt-10px text-n-primary" />
              <span v-else class="text-10px text-n-text-3 mt-8px">{{ config.texts.day(item.num) }}</span>
            </div>
          </div>
        </div>
        <div class="flex justify-center">
          <a
            v-if="progress.length"
            class="checkin-btn block mt-4 text-15px text-white text-center"
            :class="{ 'opacity-70': signed }"
            @click="onCheckIn"
          >
            <span v-if="!signed">{{ config.texts.checkIn }}</span>
            <span v-else-if="checkInDayNum > 0 && checkInDayNum === progress.length">{{ config.texts.tomorrowToGetMore }}</span>
            <span v-else>{{ config.texts.tomorrowCheckIn }} {{ tomorrowInfo?.prizeNum }}</span>
          </a>
        </div>
      </div>

      <!-- Tasks 区域 - 无卡片包裹 -->
      <div v-if="tasks && tasks.length" class="mt-16px px-14px">
        <h3 class="text-18px text-n-text-1 font-bold">
          {{ config.texts.tasks }}
        </h3>

        <div
          v-for="(item, index) in tasks"
          :key="index"
          class="flex justify-between items-center py-16px border-b-solid border-b-1px border-n-border-primary last:border-b-0"
        >
          <div>
            <div class="text-n-text-1 font-medium">
              {{ item.taskTitle }}
            </div>
            <div class="text-n-primary text-12px flex items-center mt-4px">
              <img class="w-14px h-14px" :src="config.resources.welfareGold">
              <span class="ml-1" :style="{ color: config.theme.coinsColor }">{{ `+${item.coins}` }}</span>
            </div>
          </div>

          <div
            v-if="item.buttonState !== 1"
            class="task-btn"
            @click="handleTask(item)"
          >
            {{ getBtnStatus(item) }}
          </div>
          <div v-else class="w-60px flex items-center justify-center">
            <i-assets-icon-right class="color-n-primary" />
          </div>
        </div>
      </div>
    </div>

    <WelfareGuideAddScreen :value="showGuidePopupAddHome" @input="emit('update:show-guide-popup-add-home', $event)" />
    <WelfareGuideOpen :value="showGuideOpen" @input="emit('update:show-guide-open', $event)" />

    <BasicModal v-model="modalSignedLocal">
      <div class="p-5">
        <div class="text-17px lh-2em font-bold text-center text-n-text-1">
          <p>{{ config.texts.checkInGet }} <span class="text-n-primary">{{ progressInfo?.prizeNum }}</span> {{ config.texts.coins }}</p>
        </div>
        <a
          class="modal-btn block mt-4 text-15px text-white text-center"
          :style="{ background: actionGradient }"
          @click="onCheckInOk"
        >
          {{ config.texts.ok }}
        </a>
      </div>
    </BasicModal>

    <BasicModal v-model="finishPopupLocal">
      <div class="p-5">
        <div class="text-17px lh-2em font-bold text-center text-n-text-1">
          <p>{{ completeTaskDescription }} {{ config.texts.get }} <span class="text-n-primary">{{ coins }}</span> {{ config.texts.coins }}</p>
        </div>
        <a
          class="modal-btn block mt-4 text-15px text-white text-center"
          :style="{ background: actionGradient }"
          @click="finishPopupLocal = false"
        >
          {{ config.texts.ok }}
        </a>
      </div>
    </BasicModal>

    <BasicTabbar v-if="showTabbar" tab="rewards" />
  </div>
</template>

<style scoped>
.cover {
  background-repeat: no-repeat;
  background-position: top left;
  background-size: 100% auto;
}

/* 金币数量渐变色 */
.coins-gradient {
  font-weight: bold;
  font-size: 28px;
  line-height: 28px;
  text-align: left;
  font-style: normal;
  background: v-bind(coinsGradient);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Check-in 卡片样式 */
.checkin-card {
  box-shadow: 0px 2px 10px 0px rgba(220, 224, 228, 0.4);
  border-radius: 8px;
}

/* Check-in 按钮样式 */
.checkin-btn {
  width: 315px;
  height: 40px;
  line-height: 40px;
  background: v-bind(checkinBtnGradient);
  border-radius: 20px;
}

/* Task 按钮样式 */
.task-btn {
  width: 60px;
  text-align: center;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 12px;
  color: #FFFFFF;
  background: v-bind(actionGradient);
}

/* 弹窗内按钮样式 */
.modal-btn {
  width: 100%;
  height: 40px;
  line-height: 40px;
  border-radius: 20px;
}
</style>
